import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft } from 'lucide-react';

const RagHybridDeepDive = () => {
  const [animatedPercentages, setAnimatedPercentages] = useState({
    vector: 0,
    keyword: 0,
    graph: 0,
    metadata: 0
  });

  // Smooth scroll to sections
  const scrollToMethod = (methodId) => {
    const element = document.getElementById(methodId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Animate percentages on mount
  useEffect(() => {
    const animateValue = (key, endValue, duration) => {
      const startTime = Date.now();
      const animate = () => {
        const currentTime = Date.now();
        const progress = Math.min((currentTime - startTime) / duration, 1);
        const currentValue = Math.floor(progress * endValue);
        
        setAnimatedPercentages(prev => ({
          ...prev,
          [key]: currentValue
        }));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    };

    // Start animations
    setTimeout(() => animateValue('vector', 60, 1000), 100);
    setTimeout(() => animateValue('keyword', 25, 1000), 200);
    setTimeout(() => animateValue('graph', 10, 1000), 300);
    setTimeout(() => animateValue('metadata', 5, 1000), 400);
  }, []);

  // Progressive reveal on scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.method-detail').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Handle method card hover
  const handleMethodHover = (e, isEntering) => {
    if (isEntering) {
      e.currentTarget.style.transform = 'translateY(-10px) scale(1.05)';
    } else {
      e.currentTarget.style.transform = 'translateY(0) scale(1)';
    }
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Overview Section */
        .overview-section {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            padding: 3rem;
            margin-bottom: 4rem;
            position: relative;
            overflow: hidden;
        }
        
        .overview-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 2rem;
            margin-top: 2rem;
        }
        
        .method-overview {
            text-align: center;
            padding: 2rem;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 16px;
            border: 1px solid rgba(255, 255, 255, 0.1);
            transition: all 0.3s;
            cursor: pointer;
        }
        
        .method-overview:hover {
            transform: translateY(-10px);
            border-color: rgba(102, 126, 234, 0.5);
            background: rgba(102, 126, 234, 0.1);
        }
        
        .method-icon {
            font-size: 3rem;
            margin-bottom: 1rem;
        }
        
        .method-name {
            font-size: 1.2rem;
            font-weight: bold;
            margin-bottom: 0.5rem;
        }
        
        .method-percentage {
            font-size: 2.5rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .method-description {
            font-size: 0.9rem;
            color: #888;
            margin-top: 0.5rem;
        }
        
        /* Detailed Sections */
        .method-detail {
            margin: 4rem 0;
            opacity: 0;
            transform: translateY(30px);
            animation: fadeInUp 0.8s ease forwards;
        }
        
        @keyframes fadeInUp {
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        
        .method-detail:nth-child(odd) { animation-delay: 0.2s; }
        .method-detail:nth-child(even) { animation-delay: 0.4s; }
        
        .detail-header {
            display: flex;
            align-items: center;
            gap: 2rem;
            margin-bottom: 2rem;
        }
        
        .detail-icon {
            width: 80px;
            height: 80px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2.5rem;
            flex-shrink: 0;
        }
        
        .detail-title {
            flex: 1;
        }
        
        .detail-title h2 {
            font-size: 2rem;
            margin-bottom: 0.5rem;
        }
        
        .detail-title p {
            color: #888;
        }
        
        .detail-content {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
        }
        
        /* Feature Cards */
        .features-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 1.5rem;
            margin: 2rem 0;
        }
        
        .feature-card {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 1.5rem;
            transition: all 0.3s;
        }
        
        .feature-card:hover {
            transform: scale(1.02);
            border-color: rgba(102, 126, 234, 0.5);
        }
        
        .feature-title {
            font-weight: bold;
            margin-bottom: 0.5rem;
            color: #667eea;
        }
        
        /* Code Example */
        .code-example {
            background: #1a1a1a;
            border: 1px solid #333;
            border-radius: 8px;
            padding: 1.5rem;
            margin: 1.5rem 0;
            overflow-x: auto;
        }
        
        .code-example pre {
            margin: 0;
            color: #e0e0e0;
            font-family: 'Consolas', 'Monaco', monospace;
            font-size: 0.9rem;
            line-height: 1.5;
        }
        
        .code-keyword { color: #c792ea; }
        .code-string { color: #c3e88d; }
        .code-number { color: #f78c6c; }
        .code-comment { color: #676e95; }
        
        /* Process Flow */
        .process-flow {
            background: rgba(255, 255, 255, 0.02);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin: 2rem 0;
        }
        
        .flow-steps {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 2rem 0;
        }
        
        .flow-step {
            flex: 1;
            text-align: center;
            position: relative;
        }
        
        .flow-step::after {
            content: '→';
            position: absolute;
            right: -1rem;
            top: 50%;
            transform: translateY(-50%);
            color: #667eea;
            font-size: 1.5rem;
        }
        
        .flow-step:last-child::after {
            display: none;
        }
        
        .step-number {
            width: 40px;
            height: 40px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 0.5rem;
            font-weight: bold;
        }
        
        /* Performance Metrics */
        .metrics-container {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
            border-radius: 16px;
            padding: 2rem;
            margin: 2rem 0;
        }
        
        .metrics-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 2rem;
            text-align: center;
        }
        
        .metric-value {
            font-size: 2.5rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .metric-label {
            color: #888;
            margin-top: 0.5rem;
        }
        
        /* Interactive Demo */
        .demo-section {
            background: rgba(255, 255, 255, 0.05);
            border: 2px solid rgba(102, 126, 234, 0.3);
            border-radius: 16px;
            padding: 3rem;
            margin: 3rem 0;
            text-align: center;
        }
        
        .demo-button {
            display: inline-block;
            padding: 1rem 2rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-decoration: none;
            border-radius: 8px;
            font-weight: 600;
            transition: all 0.3s;
            margin-top: 1rem;
        }
        
        .demo-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        /* Comparison Table */
        .comparison-table {
            width: 100%;
            border-collapse: collapse;
            margin: 2rem 0;
        }
        
        .comparison-table th,
        .comparison-table td {
            padding: 1rem;
            text-align: left;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .comparison-table th {
            background: rgba(102, 126, 234, 0.1);
            font-weight: bold;
        }
        
        .comparison-table tr:hover {
            background: rgba(255, 255, 255, 0.05);
        }
        
        /* Responsive */
        @media (max-width: 768px) {
            .overview-grid {
                grid-template-columns: 1fr;
            }
            
            .flow-steps {
                flex-direction: column;
                gap: 2rem;
            }
            
            .flow-step::after {
                display: none;
            }
            
            .detail-header {
                flex-direction: column;
                text-align: center;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="architecture-overview.html" className="back-link">
            <ChevronLeft size={16} /> Retour Architecture
          </a>
          <div>RAG Hybride Deep Dive</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">RAG Hybride Deep Dive</h1>
          <p className="page-subtitle">Analyse détaillée des 4 méthodes de recherche et leur synergie</p>
        </div>
        
        {/* Overview Section */}
        <div className="overview-section">
          <h2 style={{textAlign: 'center', marginBottom: '2rem'}}>Vue d'Ensemble du Système Hybride</h2>
          <div className="overview-grid">
            <div 
              className="method-overview" 
              onClick={() => scrollToMethod('vector')}
              onMouseEnter={(e) => handleMethodHover(e, true)}
              onMouseLeave={(e) => handleMethodHover(e, false)}
            >
              <div className="method-icon">🧠</div>
              <div className="method-name">Recherche Vectorielle</div>
              <div className="method-percentage">{animatedPercentages.vector}%</div>
              <div className="method-description">Compréhension sémantique</div>
            </div>
            
            <div 
              className="method-overview" 
              onClick={() => scrollToMethod('keyword')}
              onMouseEnter={(e) => handleMethodHover(e, true)}
              onMouseLeave={(e) => handleMethodHover(e, false)}
            >
              <div className="method-icon">🔍</div>
              <div className="method-name">Recherche Mots-clés</div>
              <div className="method-percentage">{animatedPercentages.keyword}%</div>
              <div className="method-description">Correspondance exacte</div>
            </div>
            
            <div 
              className="method-overview" 
              onClick={() => scrollToMethod('graph')}
              onMouseEnter={(e) => handleMethodHover(e, true)}
              onMouseLeave={(e) => handleMethodHover(e, false)}
            >
              <div className="method-icon">🕸️</div>
              <div className="method-name">Recherche Graphe</div>
              <div className="method-percentage">{animatedPercentages.graph}%</div>
              <div className="method-description">Relations cachées</div>
            </div>
            
            <div 
              className="method-overview" 
              onClick={() => scrollToMethod('metadata')}
              onMouseEnter={(e) => handleMethodHover(e, true)}
              onMouseLeave={(e) => handleMethodHover(e, false)}
            >
              <div className="method-icon">📋</div>
              <div className="method-name">Recherche Métadonnées</div>
              <div className="method-percentage">{animatedPercentages.metadata}%</div>
              <div className="method-description">Filtres précis</div>
            </div>
          </div>
        </div>
        
        {/* Vector Search Detail */}
        <div className="method-detail" id="vector">
          <div className="detail-header">
            <div className="detail-icon">🧠</div>
            <div className="detail-title">
              <h2>Recherche Vectorielle (60%)</h2>
              <p>Compréhension sémantique profonde avec embeddings</p>
            </div>
          </div>
          
          <div className="detail-content">
            <h3>Principe de Fonctionnement</h3>
            <p>La recherche vectorielle transforme les documents et requêtes en vecteurs mathématiques dans un espace à haute dimension, permettant de mesurer la similarité sémantique même avec des formulations différentes.</p>
            
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-title">🔧 Technologies</div>
                <ul>
                  <li>FAISS pour performance</li>
                  <li>Qdrant pour features avancées</li>
                  <li>Sentence-BERT embeddings</li>
                  <li>Dimension: 384 ou 768</li>
                </ul>
              </div>
              
              <div className="feature-card">
                <div className="feature-title">✨ Avantages</div>
                <ul>
                  <li>Comprend les synonymes</li>
                  <li>Gère les paraphrases</li>
                  <li>Multilingue (FR/EN)</li>
                  <li>Robuste aux fautes</li>
                </ul>
              </div>
              
              <div className="feature-card">
                <div className="feature-title">🎯 Cas d'Usage</div>
                <ul>
                  <li>Questions complexes</li>
                  <li>Concepts abstraits</li>
                  <li>Recherche thématique</li>
                  <li>Support multilingue</li>
                </ul>
              </div>
            </div>
            
            <h3>Exemple de Code</h3>
            <div className="code-example">
              <pre><span className="code-comment"># Création d'embeddings et recherche vectorielle</span>
<span className="code-keyword">from</span> sentence_transformers <span className="code-keyword">import</span> SentenceTransformer
<span className="code-keyword">import</span> faiss

<span className="code-comment"># Initialisation du modèle</span>
model = SentenceTransformer(<span className="code-string">'all-MiniLM-L6-v2'</span>)

<span className="code-comment"># Génération des embeddings</span>
embeddings = model.encode(documents, batch_size=<span className="code-number">32</span>)

<span className="code-comment"># Création de l'index FAISS</span>
dimension = embeddings.shape[<span className="code-number">1</span>]
index = faiss.IndexFlatL2(dimension)
index.add(embeddings)

<span className="code-comment"># Recherche</span>
query_vector = model.encode([query])
distances, indices = index.search(query_vector, k=<span className="code-number">20</span>)</pre>
            </div>
            
            <div className="process-flow">
              <h3>Pipeline de Traitement</h3>
              <div className="flow-steps">
                <div className="flow-step">
                  <div className="step-number">1</div>
                  <div>Tokenisation</div>
                </div>
                <div className="flow-step">
                  <div className="step-number">2</div>
                  <div>Embedding</div>
                </div>
                <div className="flow-step">
                  <div className="step-number">3</div>
                  <div>Similarité</div>
                </div>
                <div className="flow-step">
                  <div className="step-number">4</div>
                  <div>Ranking</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Keyword Search Detail */}
        <div className="method-detail" id="keyword">
          <div className="detail-header">
            <div className="detail-icon">🔍</div>
            <div className="detail-title">
              <h2>Recherche par Mots-clés BM25 (25%)</h2>
              <p>Correspondance exacte avec ranking TF-IDF optimisé</p>
            </div>
          </div>
          
          <div className="detail-content">
            <h3>Principe de Fonctionnement</h3>
            <p>BM25 est une fonction de ranking probabiliste qui étend TF-IDF en prenant en compte la longueur des documents et la saturation des termes. Parfait pour les recherches précises de codes d'erreur ou termes techniques.</p>
            
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-title">🔧 Technologies</div>
                <ul>
                  <li>Elasticsearch BM25</li>
                  <li>rank-bm25 Python</li>
                  <li>Tokenisation custom</li>
                  <li>Stemming français</li>
                </ul>
              </div>
              
              <div className="feature-card">
                <div className="feature-title">✨ Avantages</div>
                <ul>
                  <li>Termes exacts trouvés</li>
                  <li>Codes d'erreur précis</li>
                  <li>Performance rapide</li>
                  <li>Facile à débugger</li>
                </ul>
              </div>
              
              <div className="feature-card">
                <div className="feature-title">🎯 Cas d'Usage</div>
                <ul>
                  <li>Erreurs ORA-xxxxx</li>
                  <li>Références exactes</li>
                  <li>Numéros d'article</li>
                  <li>Noms de fonction</li>
                </ul>
              </div>
            </div>
            
            <h3>Formule BM25</h3>
            <div className="code-example">
              <pre><span className="code-comment"># Score BM25 pour un terme dans un document</span>
score(D,Q) = Σ IDF(qi) × (f(qi, D) × (k1 + 1)) / (f(qi, D) + k1 × (1 - b + b × |D| / avgdl))

<span className="code-comment"># Où:</span>
<span className="code-comment"># - f(qi, D) = fréquence du terme qi dans le document D</span>
<span className="code-comment"># - |D| = longueur du document D</span>
<span className="code-comment"># - avgdl = longueur moyenne des documents</span>
<span className="code-comment"># - k1 et b = paramètres de tuning (typiquement k1=1.2, b=0.75)</span></pre>
            </div>
          </div>
        </div>
        
        {/* Graph Search Detail */}
        <div className="method-detail" id="graph">
          <div className="detail-header">
            <div className="detail-icon">🕸️</div>
            <div className="detail-title">
              <h2>Recherche par Graphe (10%)</h2>
              <p>Découverte de relations et connexions cachées</p>
            </div>
          </div>
          
          <div className="detail-content">
            <h3>Principe de Fonctionnement</h3>
            <p>Le graphe de connaissances modélise les relations entre concepts, procédures, règles et modules. Il permet de découvrir des connexions non évidentes et d'enrichir les résultats avec du contexte relationnel.</p>
            
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-title">🔧 Technologies</div>
                <ul>
                  <li>Neo4j Graph DB</li>
                  <li>Cypher queries</li>
                  <li>NetworkX (fallback)</li>
                  <li>GraphML export</li>
                </ul>
              </div>
              
              <div className="feature-card">
                <div className="feature-title">✨ Avantages</div>
                <ul>
                  <li>Relations complexes</li>
                  <li>Impact analysis</li>
                  <li>Dépendances trouvées</li>
                  <li>Vue holistique</li>
                </ul>
              </div>
              
              <div className="feature-card">
                <div className="feature-title">🎯 Cas d'Usage</div>
                <ul>
                  <li>Impact réglementaire</li>
                  <li>Dépendances modules</li>
                  <li>Flux de processus</li>
                  <li>Conformité croisée</li>
                </ul>
              </div>
            </div>
            
            <h3>Structure du Graphe</h3>
            <div className="code-example">
              <pre><span className="code-comment">// Requête Cypher Neo4j</span>
MATCH (c:Cheque)-[:REGULATED_BY]->(r:Regulation)-[:ISSUED_BY]->(a:Authority)
WHERE c.type = <span className="code-string">'impayé'</span> AND a.name = <span className="code-string">'BEAC'</span>
RETURN c, r, a
LIMIT <span className="code-number">10</span>

<span className="code-comment">// Relations types:</span>
<span className="code-comment">// - USES (module -> function)</span>
<span className="code-comment">// - REGULATED_BY (procedure -> regulation)</span>
<span className="code-comment">// - DEPENDS_ON (module -> module)</span>
<span className="code-comment">// - CONFORMS_TO (process -> standard)</span></pre>
            </div>
          </div>
        </div>
        
        {/* Metadata Search Detail */}
        <div className="method-detail" id="metadata">
          <div className="detail-header">
            <div className="detail-icon">📋</div>
            <div className="detail-title">
              <h2>Recherche par Métadonnées (5%)</h2>
              <p>Filtrage précis sur attributs structurés</p>
            </div>
          </div>
          
          <div className="detail-content">
            <h3>Principe de Fonctionnement</h3>
            <p>La recherche par métadonnées permet un filtrage précis basé sur des attributs structurés comme la date, l'autorité, le type de document, ou le département. Complète parfaitement les autres méthodes.</p>
            
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-title">🔧 Technologies</div>
                <ul>
                  <li>MongoDB indexing</li>
                  <li>PostgreSQL JSONB</li>
                  <li>Faceted search</li>
                  <li>Range queries</li>
                </ul>
              </div>
              
              <div className="feature-card">
                <div className="feature-title">✨ Avantages</div>
                <ul>
                  <li>Filtres précis</li>
                  <li>Recherche rapide</li>
                  <li>Facettes dynamiques</li>
                  <li>Contraintes métier</li>
                </ul>
              </div>
              
              <div className="feature-card">
                <div className="feature-title">🎯 Cas d'Usage</div>
                <ul>
                  <li>Documents récents</li>
                  <li>Par autorité (BEAC)</li>
                  <li>Type spécifique</li>
                  <li>Niveau criticité</li>
                </ul>
              </div>
            </div>
            
            <h3>Métadonnées Indexées</h3>
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Attribut</th>
                  <th>Type</th>
                  <th>Exemple</th>
                  <th>Usage</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>document_type</td>
                  <td>Enum</td>
                  <td>procedure, code, regulation</td>
                  <td>Filtrage par type</td>
                </tr>
                <tr>
                  <td>authority</td>
                  <td>String</td>
                  <td>BEAC, BCEAO, COBAC</td>
                  <td>Source réglementaire</td>
                </tr>
                <tr>
                  <td>created_date</td>
                  <td>DateTime</td>
                  <td>2024-01-15</td>
                  <td>Recherche temporelle</td>
                </tr>
                <tr>
                  <td>department</td>
                  <td>String</td>
                  <td>IT, Compliance, Operations</td>
                  <td>Filtrage par service</td>
                </tr>
                <tr>
                  <td>criticality</td>
                  <td>Integer</td>
                  <td>1-5</td>
                  <td>Priorisation</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Fusion Algorithm */}
        <div className="method-detail">
          <div className="detail-header">
            <div className="detail-icon">⚡</div>
            <div className="detail-title">
              <h2>Algorithme de Fusion RRF</h2>
              <p>Reciprocal Rank Fusion pour combiner les résultats</p>
            </div>
          </div>
          
          <div className="detail-content">
            <h3>Principe de la Fusion</h3>
            <p>L'algorithme RRF combine les résultats des différentes méthodes en prenant en compte leur rang relatif plutôt que leur score absolu, ce qui normalise les différentes échelles de scoring.</p>
            
            <div className="code-example">
              <pre><span className="code-comment"># Algorithme Reciprocal Rank Fusion (RRF)</span>
<span className="code-keyword">def</span> reciprocal_rank_fusion(results_lists, k=<span className="code-number">60</span>):
    <span className="code-string">"""
    Fusionne plusieurs listes de résultats
    k: paramètre de lissage (typiquement 60)
    """</span>
    fusion_scores = {}
    
    <span className="code-keyword">for</span> weight, results <span className="code-keyword">in</span> results_lists:
        <span className="code-keyword">for</span> rank, doc <span className="code-keyword">in</span> enumerate(results):
            doc_id = doc[<span className="code-string">'id'</span>]
            <span className="code-keyword">if</span> doc_id <span className="code-keyword">not in</span> fusion_scores:
                fusion_scores[doc_id] = <span className="code-number">0</span>
            
            <span className="code-comment"># Score RRF pondéré</span>
            fusion_scores[doc_id] += weight * (<span className="code-number">1</span> / (k + rank + <span className="code-number">1</span>))
    
    <span className="code-comment"># Tri par score fusionné</span>
    <span className="code-keyword">return</span> sorted(fusion_scores.items(), 
                 key=<span className="code-keyword">lambda</span> x: x[<span className="code-number">1</span>], 
                 reverse=<span className="code-keyword">True</span>)</pre>
            </div>
            
            <div className="metrics-container">
              <h3>Métriques de Performance</h3>
              <div className="metrics-grid">
                <div>
                  <div className="metric-value">95.2%</div>
                  <div className="metric-label">Précision</div>
                </div>
                <div>
                  <div className="metric-value">92.7%</div>
                  <div className="metric-label">Rappel</div>
                </div>
                <div>
                  <div className="metric-value">93.9%</div>
                  <div className="metric-label">F1-Score</div>
                </div>
                <div>
                  <div className="metric-value">1.8s</div>
                  <div className="metric-label">Latence P50</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Demo Section */}
        <div className="demo-section">
          <h2>Visualisez le RAG Hybride en Action</h2>
          <p>Explorez comment les 4 méthodes collaborent pour trouver la meilleure réponse</p>
          <a href="search-flow-process.html" className="demo-button">
            Voir le Processus de Recherche →
          </a>
        </div>
      </div>
    </>
  );
};

export default RagHybridDeepDive;